import { useEffect, useRef, useState } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import axios from 'axios';
import { toast } from 'sonner';
import { routeByRole } from '@/utils/routeByRole';

const API = '/api';

export default function AuthCallback() {
  const navigate = useNavigate();
  const location = useLocation();
  const hasProcessed = useRef(false);
  const [isProcessing, setIsProcessing] = useState(true);
  const [authError, setAuthError] = useState(null);

  useEffect(() => {
    if (hasProcessed.current) return;
    hasProcessed.current = true;

    const processAuth = async () => {
      try {
        // Try extracting session_id from hash fragment first
        const hash = window.location.hash;
        const params = new URLSearchParams(hash.substring(1));
        let sessionId = params.get('session_id');

        // Fallback: check query params (iOS sometimes strips hash on redirects)
        if (!sessionId) {
          const queryParams = new URLSearchParams(window.location.search);
          sessionId = queryParams.get('session_id');
        }

        // Fallback: check the full URL for session_id pattern
        if (!sessionId) {
          const fullUrl = window.location.href;
          const match = fullUrl.match(/session_id=([^&&#]+)/);
          if (match) sessionId = match[1];
        }

        if (!sessionId) {
          console.error('[Auth] No session_id found. URL:', window.location.href);
          setAuthError('no_session_id');
          setIsProcessing(false);
          return;
        }

        // Determine role from pathname
        const isTeacher = location.pathname.includes('/teacher');
        const isStudent = location.pathname.includes('/student');
        const role = isTeacher ? 'teacher' : isStudent ? 'student' : 'parent';

        // Exchange session_id for user session
        // Use relative path to avoid cross-origin issues with custom domains
        let response;
        try {
          response = await axios.post(
            `${API}/auth/session`,
            { session_id: sessionId, role }
          );
        } catch (firstError) {
          // Retry once after 1.5s — handles transient upstream auth service issues
          if (firstError?.response?.status === 502) {
            console.log('[Auth] First attempt got 502, retrying in 1.5s...');
            await new Promise(r => setTimeout(r, 1500));
            response = await axios.post(
              `${API}/auth/session`,
              { session_id: sessionId, role }
            );
          } else {
            throw firstError;
          }
        }

        const data = response.data;

        // Store session_token in localStorage as iOS cookie fallback
        if (data.session_token) {
          localStorage.setItem('yukti_session_token', data.session_token);
        }

        const user = data;

        // AUTH.2: delegate post-auth navigation to routeByRole() — prefers
        // backend-provided `next_route` hint, falls back to client logic.
        const target = routeByRole(user);
        navigate(target, { state: { user }, replace: true });
      } catch (error) {
        console.error('[Auth] Error:', error);
        const detail = error?.response?.data?.detail || '';
        if (error?.response?.status === 403 && detail.includes('Registration limit')) {
          toast.error('Yukti is currently in early access and has reached maximum users. Please try again later.');
          navigate('/', { replace: true });
        } else if (error?.response?.status === 502 || detail.includes('invalid response')) {
          setAuthError('service_error');
          setIsProcessing(false);
        } else {
          setAuthError('exchange_failed');
          setIsProcessing(false);
        }
      }
    };

    processAuth();
  }, [navigate, location]);

  // Show retry UI if auth failed
  if (authError) {
    return (
      <div className="adult-layout min-h-screen flex items-center justify-center bg-gradient-to-b from-orange-50 to-white">
        <div className="text-center p-8 max-w-sm">
          <div className="w-16 h-16 bg-red-100 rounded-full flex items-center justify-center mx-auto mb-4">
            <svg className="w-8 h-8 text-red-500" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-2.5L13.732 4c-.77-.833-1.964-.833-2.732 0L4.082 16.5c-.77.833.192 2.5 1.732 2.5z" />
            </svg>
          </div>
          <h2 className="text-xl font-semibold text-gray-800 mb-2">Sign-in interrupted</h2>
          <p className="text-gray-500 text-sm mb-6">
            {authError === 'no_session_id'
              ? "We couldn't complete the sign-in. This can happen on some mobile browsers."
              : authError === 'service_error'
              ? "Our authentication service is temporarily unavailable. Please try again in a moment."
              : "Something went wrong during sign-in. Please try again."}
          </p>
          <button
            data-testid="auth-retry-btn"
            onClick={() => {
              // REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
              // Trailing slash is REQUIRED for iOS Safari (WebKit bug #24701)
              const redirectUrl = window.location.origin + '/auth/callback/parent/';
              window.location.href = `https://auth.emergentagent.com/?redirect=${encodeURIComponent(redirectUrl)}`;
            }}
            className="w-full bg-orange-600 text-white py-3 px-6 rounded-xl font-medium hover:bg-orange-700 transition-colors mb-3"
          >
            Try Again
          </button>
          <button
            data-testid="auth-back-btn"
            onClick={() => navigate('/', { replace: true })}
            className="w-full text-gray-500 py-2 text-sm hover:text-gray-700 transition-colors"
          >
            Back to Home
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="adult-layout min-h-screen flex items-center justify-center">
      <div className="text-center">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto mb-4"></div>
        <p className="text-gray-600">Completing sign in...</p>
      </div>
    </div>
  );
}
